import React, { useState, useEffect } from "react"
import { StaticImage } from "gatsby-plugin-image"

import Space from "./space"
import Logo from "./logo"
import Text from "./text"
import ButtonFill from "./button-fill"
import Link from "./link"

import { animate, easeOutPower3 } from "../utils/animate"
import { onClickAddFriend } from "../utils/hook"

type Props = {
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmContent: string
}

const HeaderNavigation = ({ utmSource, utmMedium, utmCampaign, utmContent }: Props) => {
  const [isMobileNavigationOpened, setMobileNavigationOpened] = useState(false)
  const openMobileNavigation = () => {
    setMobileNavigationOpened(true)
  }
  const closeMobileNavigation = () => {
    setMobileNavigationOpened(false)
  }
  useEffect(() => {
    if (isMobileNavigationOpened) {
      document.body.classList.add("suppress-scroll")
      const elements = document.querySelectorAll<HTMLElement>(".slide-fade-in")
      console.log(elements.length)
      elements.forEach((el, i) => {
        console.log(el)
        animate({
          timing: easeOutPower3,
          draw: progress => {
            // x = 40 -> 0
            el.style.transform = `translateX(${(1 - progress) * 40}px)`
            // autoAlpha = 0 -> 1
            el.style.opacity = `${progress}`
            el.style.visibility = "inherit"
          },
          duration: 250 + i * 50,
        })
      })
    } else {
      document.body.classList.remove("suppress-scroll")
    }
  }, [isMobileNavigationOpened])

  return (
    <div>
      <Space sizeY="4" />
      <div className="grid grid-cols-16 gap-0">
        <div className="col-start-2 col-end-16">
          <div className="flex items-center justify-between">
            <Link href="/">
              <Logo />
            </Link>
            <nav role="navigation" className="hidden lg:block">
              <ul className="flex items-center space-x-5">
                <li>
                  <Link href="/#voc">
                    <Text fontSize="sm" fontBold={true}>
                      ユーザーの声
                    </Text>
                  </Link>
                </li>
                <li>
                  <Link href="/#premium">
                    <Text fontSize="sm" fontBold={true}>
                      ペアケアプレミアム
                    </Text>
                  </Link>
                </li>
                <li>
                  <Link href="/media/column/">
                    <Text fontSize="sm" fontBold={true}>
                      コラム
                    </Text>
                  </Link>
                </li>
                <li>
                  <Space sizeX="1" />
                </li>
                <li>
                  <ButtonFill
                    onClick={() => {
                      onClickAddFriend({ utmSource, utmMedium, utmCampaign, utmContent })
                    }}
                    classes="py-3 pl-5 pr-6"
                    roundedSize="full"
                    color="green-800"
                  >
                    <div className="flex items-center">
                      <StaticImage
                        src="../images/button-icon-line.png"
                        alt="LINE"
                        placeholder="none"
                        width={20}
                      />
                      <Text fontSize="sm" color="white" classes="ml-1">
                        友だちに追加
                      </Text>
                    </div>
                  </ButtonFill>
                </li>
              </ul>
            </nav>
            <div className="block lg:hidden">
              {!isMobileNavigationOpened ? (
                <button
                  type="button"
                  className="p-2 -m-2 focus:outline-none"
                  aria-expanded={isMobileNavigationOpened}
                  aria-controls="mobile-navigation"
                  onClick={openMobileNavigation}
                >
                  <span className="bg-black nav-menu-icon-bar" />
                  <span className="bg-black nav-menu-icon-bar" />
                  <span className="bg-black nav-menu-icon-bar" />
                </button>
              ) : (
                <div className="z-40 bg-white overflow-y-scroll fixed top-0 right-0 w-full h-full grid grid-cols-16 gap-0">
                  <div className="col-start-2 col-end-16 flex flex-col py-5">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="slide-fade-in p-2 -m-2 focus:outline-none"
                        aria-expanded={isMobileNavigationOpened}
                        aria-controls="mobile-navigation"
                        onClick={closeMobileNavigation}
                      >
                        <span className="bg-black nav-menu-icon-bar is-close" />
                        <span className="bg-black nav-menu-icon-bar is-close" />
                        <span className="bg-black nav-menu-icon-bar is-close" />
                      </button>
                    </div>
                    <Space sizeY="10" />
                    <nav
                      id="mobile-navigation"
                      role="navigation"
                      className="h-full flex flex-col justify-between px-2"
                    >
                      <ul className="flex flex-col space-y-5">
                        <li className="slide-fade-in">
                          <Link href="/#voc" onClick={closeMobileNavigation}>
                            <Text fontSize="2.5xl" fontBold={true}>
                              ユーザーの声
                            </Text>
                          </Link>
                        </li>
                        <li className="slide-fade-in">
                          <Link
                            href="/#premium"
                            onClick={closeMobileNavigation}
                          >
                            <Text fontSize="2.5xl" fontBold={true}>
                              ペアケアプレミアム
                            </Text>
                          </Link>
                        </li>
                        <li className="slide-fade-in">
                          <Link href="/media/column/">
                            <Text fontSize="2.5xl" fontBold={true}>
                              コラム
                            </Text>
                          </Link>
                        </li>
                        <li className="slide-fade-in">
                          <Space sizeY="5" />
                        </li>
                        <li className="slide-fade-in">
                          <ButtonFill
                            onClick={() => {
                              onClickAddFriend({
                                utmSource,
                                utmMedium,
                                utmCampaign,
                                utmContent,
                              })
                            }}
                            classes="py-3 pl-5 pr-6"
                            roundedSize="full"
                            color="green-800"
                          >
                            <div className="flex items-center">
                              <StaticImage
                                src="../images/button-icon-line.png"
                                alt="LINE"
                                placeholder="none"
                                width={30}
                              />
                              <Text fontSize="xl" color="white" classes="ml-1">
                                友だちに追加
                              </Text>
                            </div>
                          </ButtonFill>
                        </li>
                      </ul>
                      <div className="slide-fade-in py-5">
                        <Link href="/">
                          <Logo />
                        </Link>
                      </div>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Space sizeY="4" />
    </div>
  )
}

export default HeaderNavigation
